// The eight built-in logo marks as data: original glyphs on a 24-unit grid,
// every part an SVG path string. CompanyMark.vue draws them as SVG; the end
// outputs (plan PNG, program card, businessBrand()) draw the same paths on a
// canvas with Path2D, so the printed mark is the one on screen.
//
// `fill`/`stroke` name a paint: 'glyph' is the mark's colour, 'cut' is the
// ground it sits on (leaf rib, wheel rims, laces); null means none.

import type { LogoMark } from './identity';

type Paint = 'glyph' | 'cut' | null;

export interface MarkPart {
  d: string;
  fill: Paint;
  stroke: Paint;
  /** Stroke width in grid units. */
  w?: number;
  opacity?: number;
}

/** A circle as a path (two arcs). */
const circle = (cx: number, cy: number, r: number): string =>
  `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0Z`;
/** A rectangle with corner radius 1, as a path. */
const rect1 = (x: number, y: number, w: number, h: number): string =>
  `M${x + 1} ${y}h${w - 2}a1 1 0 0 1 1 1v${h - 2}a1 1 0 0 1-1 1h${-(w - 2)}a1 1 0 0 1-1-1v${-(h - 2)}a1 1 0 0 1 1-1Z`;

const solid = (d: string, opacity?: number): MarkPart => ({ d, fill: 'glyph', stroke: null, ...(opacity ? { opacity } : {}) });
const line = (d: string, w: number, paint: Paint = 'glyph'): MarkPart => ({ d, fill: null, stroke: paint, w });
const rim = (cx: number, cy: number, r: number): MarkPart => ({ d: circle(cx, cy, r), fill: 'glyph', stroke: 'cut', w: 1.2 });

export const MARK_PARTS: Record<LogoMark, MarkPart[]> = {
  'gum-leaf': [
    solid('M5.5 19.5C6 11.5 11 5 20 3.5 19.5 12 13.5 18.5 5.5 19.5Z'),
    line('M5.5 19.5C9.5 14 13.5 9.5 20 3.5', 1.2, 'cut'),
    line('M5.5 19.5 3 22', 2),
  ],
  mower: [
    line('M13 13 18.5 3.5M16.5 7h4', 2),
    solid('M3 17v-2.5C3 13.7 3.7 13 4.5 13H15c1.7 0 3 1.3 3 3v1Z'),
    rim(6.5, 18.5, 2.5),
    rim(15, 18.5, 2.5),
  ],
  sun: [
    solid(circle(12, 12, 4.5)),
    line('M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1', 2),
  ],
  stripes: [
    solid(rect1(3, 4, 5, 16)),
    solid(rect1(9.5, 4, 5, 16), 0.45),
    solid(rect1(16, 4, 5, 16)),
  ],
  ute: [
    solid('M1.5 17v-4.5L3.5 12 6.5 7H13v5h9.5v5Z'),
    { d: 'M8 8.8h3.5V12H6.2Z', fill: 'cut', stroke: null },
    rim(6.5, 17.5, 2.6),
    rim(17.5, 17.5, 2.6),
  ],
  boot: [
    solid('M6.5 3H12v10l6.8 1.9c1.6.5 2.7 1.9 2.7 3.6V20H5.3l.7-6.2Z'),
    line('M5.3 20h16.2', 1.4, 'cut'),
    line('M8.5 6.5h3.5M8.5 9.5h3.5', 1.2, 'cut'),
  ],
  hat: [
    solid('M7.5 14.5C7.5 9 9 6.5 12 6.5s4.5 2.5 4.5 8Z'),
    line('M10 7.2 12 9l2-1.8', 1.2, 'cut'),
    solid('M1.5 14.8c3.5 3.3 17.5 3.3 21 0-3.7-1.4-17.3-1.4-21 0Z'),
    line('M7.6 13.2h8.8', 1.2, 'cut'),
  ],
  star: [
    solid('M12 2.5l2.9 6 6.6.8-4.9 4.5 1.3 6.5L12 17.1l-5.9 3.2 1.3-6.5-4.9-4.5 6.6-.8Z'),
  ],
};

/**
 * Draw a mark on a canvas: a rounded tile in `ground` with the glyph in
 * `glyph`, filling the square at (x, y) of side `size`. Browser only (Path2D).
 */
export function drawMarkTile(
  ctx: CanvasRenderingContext2D, mark: LogoMark, x: number, y: number, size: number, ground: string, glyph: string,
): void {
  // The tile is the SVG's -4..28 box: 32 units with a corner radius of 4.
  const k = size / 32;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(k, k);
  ctx.fillStyle = ground;
  ctx.beginPath();
  ctx.roundRect(0, 0, 32, 32, 4);
  ctx.fill();
  ctx.translate(4, 4);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const paint = (p: Paint): string => (p === 'cut' ? ground : glyph);
  for (const part of MARK_PARTS[mark] ?? MARK_PARTS.star) {
    const path = new Path2D(part.d);
    ctx.globalAlpha = part.opacity ?? 1;
    if (part.fill) { ctx.fillStyle = paint(part.fill); ctx.fill(path); }
    if (part.stroke) { ctx.strokeStyle = paint(part.stroke); ctx.lineWidth = part.w ?? 1; ctx.stroke(path); }
  }
  ctx.restore();
}
